<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import GwUiType from '@/components/lib/GwTable/GwUiType.vue'
import * as R from 'remeda'
import type { ID } from '@/types/basic'
import {
  getUniqueValues,
  UNIQUE_VALUES_FIELDS,
  type FieldName,
} from '@/store/api/gql/unique_values/queries'
import LoaderCircles from '@/components/Loaders/LoaderCircles.vue'
import { t } from '@/locales/i18n'
import type { FilterValue, TFilterField, TUiTypeValues } from '../../../../types/api/reports'
import type { TFilterEmitValue } from './utils'
import GwBtnGroup, { type GwBtnGroupProps } from '../../../lib/GwBtnGroup/GwBtnGroup.vue'

export type ItemBtnGroupProps = {
  field: TFilterField
  value: FilterValue
  isActive: boolean
  options?: GwBtnGroupProps['options']
  boxVariant?: GwBtnGroupProps['boxVariant']
  direction?: GwBtnGroupProps['direction']
  btnVariant?: GwBtnGroupProps['btnVariant']
}

const props = withDefaults(defineProps<ItemBtnGroupProps>(), {
  boxVariant: 'box_column', //
  direction: 'vertical',
})

const emit = defineEmits<{
  'update:filterField': [TFilterEmitValue]
}>()

const isSingle = computed(() => {
  const filterType =
    props.field.api_filters.find(({ field_name }) => field_name === props.field.field_name)
      ?.filter_type || 'SINGLE_CHOICE'
  return filterType === 'SINGLE_CHOICE'
  // fallback to single choice in case of mismatch in filter_type
})

const hasUniqueValues = computed(
  () => UNIQUE_VALUES_FIELDS.includes(props.field.field_name) && !props.options,
)

const { data, isLoading } = useQuery({
  queryKey: ['available_options', props.field.id],
  queryFn: getUniqueValues(props.field.field_name as FieldName),
  throwOnError: true,
  enabled: hasUniqueValues.value,
})

const uiValueType = computed(() => props.field.ui_value_type)

const options = computed(() => {
  if (props.options) {
    return props.options
  }

  if (hasUniqueValues.value) {
    return R.pipe(
      data.value?.unique_values[props.field.field_name] ?? [],
      R.filter(item => !Array.isArray(item) || item.length > 0),
      props.field.field_name === 'preflop_game_depth'
        ? R.sortBy(item => (R.isString(item) ? parseFloat(item) : item))
        : R.identity,
      R.map(id => ({
        id,
      })),
    )
  }

  const availableOptions = (
    (props.field.available_choices || props.field.available_type_choices) ??
    []
  ).map(id => ({
    id,
  }))

  return availableOptions
})

const handleUpdateValue = (value: string) => {
  emit('update:filterField', {
    field: props.field, //
    value,
    is_active: props.isActive,
  })
}
</script>
<template>
  <LoaderCircles
    v-if="isLoading"
    size="large"
    class="ma-auto"
  />
  <GwBtnGroup
    v-else
    :boxVariant
    :direction
    :btnVariant
    :isSingle
    :options
    :value
    :tstPrefix="field.field_name"
    @update:value="handleUpdateValue"
  >
    <template #default="{ item }">
      <GwUiType
        :value="item.id"
        :uiType="uiValueType"
      />
      <slot />
    </template>
  </GwBtnGroup>
</template>
